// collection of methods used by all "TrafficReport" modules
import React from 'react';
import _ from 'lodash';
import { XId, modifyHash, stopEvent, encURI } from '../base/utils/miscutils';

import ActionMan from '../plumbing/ActionMan';
import C from '../C';
import printer from '../base/utils/printer';
import DataStore from '../base/plumbing/DataStore';
import SimpleTable, {CellFormat} from '../base/components/SimpleTable';
import ServerIO from '../plumbing/ServerIO';
import Misc from '../base/components/Misc';

const typeFromParam = param => {
	return {vert: C.TYPES.Advert, pub: C.TYPES.Publisher, cid:C.TYPES.Charity}[param];
};

/**
 * Processes data into form ready for use by DonationDataDisplay
 * output -- {sampleKey: {logo: '', donations: 0, name:''}}
 * Will remove charities with donations total less than 1 pence
 */
const formatCharityData = (donationsdataByCharity) => {
	const charities = Object.keys(donationsdataByCharity);
	let formattedCharityData = charities
		.reduce((result, k) => {
			// skip if donation amount less than £0.01
			if (donationsdataByCharity[k].value < 0.01) {
				return result;
			}
			
			// if key is unset, don't call getDataItem
			if (k === 'unset') {
				result[k] = {
					donations: donationsdataByCharity[k],
					logo: '',
					name: ''
				};
				return result;
			}
			// otherwise call getDataItem, if nothing returned, fill in blank data
			let data = ActionMan.getDataItem({type:C.TYPES.NGO, id:k, status:C.KStatus.PUBLISHED, swallow:true}).value || {[k]: {logo: '', name: ''}};
			result[k] = {
				donations: donationsdataByCharity[k],
				logo: data.logo,
				name: data.displayName || data.name
			};
			return result;
		}, {});

	return formattedCharityData;
};

/** Obj should have structure {"charity":{"donation":{"value": 0}}}
 *  TODO: make this method more general
 *  obj having a nested structure is the only problem with this
 */
const sortDonationsByValue = (obj) => {
	const sortedKeys = Object.keys(obj)
		.sort((a, b) => obj[b].donations.value - obj[a].donations.value);

	return sortedKeys.reduce((result, k) => {
		result[k] = obj[k];
		return result;
	}, {});
};

function DonationDataDisplay({data}) {
	if(!data || _.isEmpty(data.by_cid) || !data.total) return null;
	
	const {by_cid, total} = data;
	const formattedCharityData = sortDonationsByValue(formatCharityData(by_cid));

	//need to iterate over, and unpack data
	const cardStyle = Object.assign({width:'300px', height:'250px', display: 'inline-block', marginRight: '10px'}, {});
	return (
		<div className="container well-lg">
			<h1>Total donation amount:
				{/* {displayValue(Object.keys(data).reduce((total, v) => total+=data[v].donations, 0))} */}
				<Misc.Money amount={total.value} />
			</h1>
			{
				Object.keys(formattedCharityData)
					.map((k) => {
						return (
							<DataCard
								header={formattedCharityData[k].name || k}
								datum={formattedCharityData[k].donations.value}
								vpos="top"
								stuff={{className: 'thumbnail', style: cardStyle}}
								key={k}
							>
								{formattedCharityData[k].logo ? <img style={{maxHeight:'125px', width:'auto'}} src={formattedCharityData[k].logo} alt={`Logo for ${k}`} /> : null}
							</DataCard>
						);
					})
			}
		</div>
	);
}

/**Note, 04/06/18 -- now only used to display breakdown of charity data
* Mainly used to display title and a value
* Made so that other elements (such as pictures) are easy to add in
* @param vpos allows you to specify if children appear above or below header and data
* means that I can use datacard both for data overview and donation display*/
function DataCard({header, datum, children, vpos = 'bottom', stuff}) {
	return (
		<div {...stuff} className={`col-md-4 well-lg large-text ${stuff.className || ''}`}>
			{vpos === 'top' ? children : null}
			{typeof header !== 'undefined' && typeof datum !== 'undefined' ?
				<h3 className="text-center" style={{wordWrap:"break-word"}}>{header}{': '}
					{/* {displayValue(datum)} */}
					<Misc.Money amount={datum} />
				</h3>
				: null}
			{vpos === 'bottom' ? children : null}
		</div>
	);
}

/**
* nice looking numbers
* @param {*} v
*/
const displayValue = (v) => {
	if (_.isNumber(v)) {
		// 1 decimal place
		v = Math.round(v*10)/10;
		// commas
		v = printer.prettyNumber(v, 10);
	}
	return v;
};

/** Returns Date object. Counts d, m, y back from today's date
*/
const pastCalendarDate = (day = 0, month = 0) => {
	const currentDate = new Date();
	//Need to set these in order to prevent path endlessly updating itself
	currentDate.setMilliseconds(0);
	currentDate.setSeconds(0);
	currentDate.setMinutes(0);
	currentDate.setHours(0);

	let newDate = currentDate;
	newDate.setDate(currentDate.getDate() - day);
	newDate.setMonth(currentDate.getMonth() - month);

	return newDate;
};

export {
	DataCard,
	displayValue,
	DonationDataDisplay,
	formatCharityData,
	pastCalendarDate,
	typeFromParam,
	sortDonationsByValue
};
